// Stock Take Start Confirmation Dialog
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoTooltip } from "@/components/info-tooltip";
import { 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  ClipboardList,
  Package,
  MapPin,
  DollarSign
} from "lucide-react";
import { StockItem } from "@/types/stock-item";
import { StockTakeStartValidationResult } from "@/lib/stock-take-start-validation";

interface StockTakeStartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  validationResult: StockTakeStartValidationResult;
  onConfirm: () => void;
}

export function StockTakeStartDialog({
  open,
  onOpenChange,
  validationResult,
  onConfirm,
}: StockTakeStartDialogProps) {
  const { canProceed, validItems, excludedItems, warnings } = validationResult;

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Start Stock Take
          </DialogTitle>
          <DialogDescription>
            {canProceed
              ? `Create a stock take with ${validItems.length} item${validItems.length !== 1 ? "s" : ""}`
              : "Cannot start stock take - no valid items selected"}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-4">
            {/* Valid Items Section */}
            {validItems.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <h4 className="font-semibold text-sm">
                    Items to Include ({validItems.length})
                  </h4>
                </div>
                <div className="space-y-2">
                  {validItems.slice(0, 5).map((item) => (
                    <div
                      key={item.code}
                      className="p-3 border rounded-lg bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-semibold text-sm">
                              {item.code}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {item.category || "Uncategorized"}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {item.description}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              <span>
                                {item.warehouseCode || "N/A"} - {item.locationCode || "N/A"}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Package className="h-3 w-3" />
                              <span>Qty: {item.physicalQuantity || 0}</span>
                            </div>
                            {item.averageCost && (
                              <div className="flex items-center gap-1">
                                <DollarSign className="h-3 w-3" />
                                <span>${item.averageCost.toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                      </div>
                    </div>
                  ))}
                  {validItems.length > 5 && (
                    <div className="text-sm text-muted-foreground text-center py-2">
                      ... and {validItems.length - 5} more item{validItems.length - 5 !== 1 ? "s" : ""}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Excluded Items Section */}
            {excludedItems.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <h4 className="font-semibold text-sm">
                      Excluded Items ({excludedItems.length})
                    </h4>
                  </div>
                  <div className="space-y-2">
                    {excludedItems.slice(0, 3).map((item) => (
                      <div
                        key={item.code}
                        className="p-3 border rounded-lg bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono font-semibold text-sm">
                                {item.code}
                              </span>
                              <Badge variant="destructive" className="text-xs">
                                Inactive
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">
                              {item.description}
                            </p>
                          </div>
                          <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                    {excludedItems.length > 3 && (
                      <div className="text-sm text-muted-foreground text-center py-2">
                        ... and {excludedItems.length - 3} more excluded item
                        {excludedItems.length - 3 !== 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Warnings Section */}
            {warnings.length > 0 && (
              <>
                <Separator />
                <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-sm">
                    <ul className="list-disc list-inside space-y-1">
                      {warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              </>
            )}

            {/* Info Section */}
            {canProceed && (
              <>
                <Separator />
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <InfoTooltip content={
                    <div className="space-y-2">
                      <p className="font-semibold">What happens next:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>You'll be taken to the Stock Take creation page</li>
                        <li>Selected items will be pre-populated</li>
                        <li>You can configure stock take details</li>
                        <li>You can add more items or remove items</li>
                        <li>Begin counting when ready</li>
                      </ul>
                    </div>
                  } />
                  <span>Hover for next steps</span>
                </div>
              </>
            )}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canProceed}>
            {canProceed ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Continue
              </>
            ) : (
              "Cannot Proceed"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
